/*
 * maps.ts — lazy-inject the venue Google Maps <iframe> (perf + privacy).
 *
 * Markup (Views/Shared/content/_Venue.cshtml):
 *   <div class="map-embed" data-map-src="https://www.google.com/maps?...output=embed"
 *        data-map-title="...">
 *     <button type="button" class="map-embed__load btn-wf btn-wf--ghost">Load map</button>
 *   </div>
 *
 * The iframe is created on first click, or when the block scrolls near the
 * viewport — whichever comes first. No-JS: the "open in Google Maps" link in the
 * partial is always present, so the map is never the only way through.
 */

export function initMaps(): void {
  const blocks = Array.from(document.querySelectorAll<HTMLElement>(".map-embed[data-map-src]"));
  if (!blocks.length) return;

  const inject = (block: HTMLElement): void => {
    if (block.dataset.mapLoaded) return;
    block.dataset.mapLoaded = "1";

    const src = block.dataset.mapSrc;
    if (!src) return;

    const frame = document.createElement("iframe");
    frame.src = src;
    frame.title = block.dataset.mapTitle || "Map";
    frame.loading = "lazy";
    frame.referrerPolicy = "no-referrer-when-downgrade";
    frame.setAttribute("allowfullscreen", "");
    frame.className = "map-embed__frame";

    block.querySelector(".map-embed__load")?.remove();
    block.appendChild(frame);
    block.classList.add("is-loaded");
  };

  blocks.forEach((block) => {
    block.querySelector(".map-embed__load")?.addEventListener("click", () => inject(block));
  });

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            inject(entry.target as HTMLElement);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "300px" }
    );
    blocks.forEach((block) => io.observe(block));
  }
}
